import {useHistory, useQueryParamsCallback} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React, {useCallback, useEffect, useRef} from 'react';

const DEFAULT_SCROLL_TO_SECTION_KEY = 'scrollToSection';

export const useScrollToSection = (key: string) => {
  const keyParam = key ?? DEFAULT_SCROLL_TO_SECTION_KEY;
  const history = useHistory();
  const getQueryParams = useQueryParamsCallback();
  const ref = useRef<HTMLDivElement>(null);

  const getUpdatedSearchParams = useCallback(
    () => new URLSearchParams(getQueryParams()),
    [getQueryParams],
  );

  const disableScrollToSection = useCallback(() => {
    const updatedQueryParams = getUpdatedSearchParams();
    updatedQueryParams.delete(keyParam);

    history.replace({
      search: updatedQueryParams.toString(),
    });
  }, [getUpdatedSearchParams, history, keyParam]);

  const scrollToSection = () => {
    const updatedQueryParams = getUpdatedSearchParams();
    updatedQueryParams.set(keyParam, '1');

    history.replace({
      search: updatedQueryParams.toString(),
    });
  };

  const shouldScrollToSection = getQueryParams().has(keyParam);

  useEffect(() => {
    if (shouldScrollToSection && ref.current) {
      disableScrollToSection();
      const top = ref.current?.getBoundingClientRect().top + window.scrollY;
      if (top) {
        window.scrollTo({
          top,
          behavior: 'smooth',
        });
      }
    }
  }, [disableScrollToSection, shouldScrollToSection]);

  return {
    scrollToSection,
    disableScrollToSection,
    shouldScrollToSection,
    ScrollToTarget: React.memo(function Component() {
      return <ScrollToTarget ref={ref} />;
    }),
  };
};

const ScrollToTarget = styled.div`
  position: absolute;
  height: 1px;
  margin-top: -4rem;
`;
